/*

Render the maintenance plan alternative chart for the LCCA Alternatives page.

Tightly coupled to the AlternativeContainer/ActivityContainer VueJS components, which generate all the DOM elements
that this file looks for during render.

This was necessary in order to be able to fire VueJS events and pass data through components while still using
d3 to render the pretty pictures.

NOTE: Major difference from the prior version - this file is called once per alternative, and is called by the
component associated with the alternative to be rendered. Prior to this version this file was responsible for
building the DOM and rendering all charts simultaneously.

 */




import {ILccaActivityImperial, ILccaAlternativeImperial} from "@/modules/lcca/interfaces/lccaCalculated";

//@ts-ignore
import {pickColors} from "@/modules/lcca/lib/pickColors.js"

var d3 = Object.assign(
  {},
  require('d3-shape'),
  require('d3-selection'),
  require('d3-axis'),
  require('d3-scale'),
  require('d3-array'),
  require('d3-drag')
);

export const plotActivities = function (alternative: ILccaAlternativeImperial,
                                        analysisPeriod: number,
                                        beginningAnalysisPeriod: number){
                                        // allAlternatives: ILccaAlternative[]) {

  const altBaseId = '#alt_' + alternative.index;

  const barHeight = 40;
  const barSpacing = 5;
  const margin = {top: 20, right: 20, bottom: 20, left: 10};

  let actNameLength = 0;

  const defaultStartYear = 0,
    defaultServiceLife = 10,
    defaultStructuralLife = 20;

  // add default start years and lengths for createMonteCarloChart
  alternative.activities.forEach(function (activity: ILccaActivityImperial) {
    activity.startYear = (activity.hasOwnProperty("startYear")) ? activity.startYear : defaultStartYear;
    activity.serviceLife_yrs = (activity.hasOwnProperty("serviceLife_yrs")) ? activity.serviceLife_yrs : defaultServiceLife;
    activity.structuralLife_yrs = (activity.hasOwnProperty("structuralLife_yrs")) ? activity.structuralLife_yrs : defaultStructuralLife;
    actNameLength = (activity.name.length > 8 && activity.name.length < 26 && activity.name.length > actNameLength) ? activity.name.length : actNameLength;
  });

  const containerDiv = document.getElementById("alt_" + alternative.index + "_container");

  const div = d3.select("#alt_" + alternative.index);
  const svg = div.select("#alt_" + alternative.index + "_canvas")
    .attr("style", "padding-left: 0px")// " + (actNameLength * 3) + "
    .attr("width", containerDiv ? containerDiv.offsetWidth : 1000)
    .attr("height", (alternative.activities.length + 1) * (barHeight + barSpacing) + margin.top + margin.bottom);

  const width = svg.attr("width") - margin.left - margin.right,
    height = svg.attr("height") - margin.top - margin.bottom;

  const actNamesObj: { [index: number]: string } = {};
  alternative.activities.forEach(function (activity: ILccaActivityImperial, i) {
    actNamesObj[i] = activity.name;
  });

  // Scale for y axis. Range refers to the number of pixels, Domain to the maximum value needed on the y-axis (assuming y-axis is 0 to max value)
  const yDomain = Object.keys(actNamesObj).map((k: string) => (parseFloat(k)));
  const y = d3.scaleBand()
    .domain(yDomain)
    .rangeRound([0, height])
    .padding(0.5);


  // Scale for x axis. Range refers to the number of pixels, Domain to the maximum value needed on the x-axis (assuming x-axis is 0 to max value)
  const x = d3.scaleLinear()
    .rangeRound([0, width])
    .domain([-actNameLength / 2, analysisPeriod * 1.5]);

  // Translate from pixels back to years on the x-axis.
  const xInverse = d3.scaleLinear()
    .domain([0, width])
    .range([-actNameLength / 2, analysisPeriod * 1.5]);

  // Get Pavia colors
  const colors = pickColors(alternative.activities.length);

  // Add graph group to SVG
  // const g = svg.append("g")
  const g = d3.select('#alt_' + alternative.index + '_group')
    .attr("transform", "translate(" + margin.left + "," + margin.top + ")");

  // Add text element to put alerts in
  const draggingText = d3.select('#alt_' + alternative.index + '_drag')
    .attr("style", "font-weight:bold")
    .attr("class", "draggingText")
    .attr("y", 10);

  //TODO: Does this remove all httpErrors?
  // displayAlternativeErrors({}, '', false);

  // function makeTestAlt(activity: ILccaActivityImperial, key: string) {
  //   const altClone = JSON.parse(JSON.stringify(allAlternatives[alternative.index]));
  //   if (key === "startYear") {
  //     altClone.activities[activity.index][key] = dragToYears(0);
  //   } else { // key === structuralLife_yrs || key === serviceLife_yrs
  //     altClone.activities[activity.index][key] = dragToYears(activity.startYear);
  //   }
  //   return altClone;
  // }

  function dragToYears(lowerLim: number) {
    const xPosition = d3.mouse(g.node())[0];
    return (Math.round(xInverse(xPosition)) - lowerLim >= 0) ? Math.round(xInverse(xPosition)) - lowerLim : 0;
  }

  // function displayAlternativeErrors(activity: ILccaActivityImperial, key: string, mousePositionNeeded: boolean) {
  //   let paveLifeObj;
  //   if (mousePositionNeeded) {
  //     const testAlt = makeTestAlt(activity, key);
  //     paveLifeObj = checkAltPaveLife(testAlt, analysisPeriod);
  //   } else if (!mousePositionNeeded) {
  //     paveLifeObj = checkAltPaveLife(allAlternatives[alternative.index], analysisPeriod);
  //   }
  //
  //   if (paveLifeObj && paveLifeObj.httpErrors.length !== 0) {
  //     //TODO: Mechanism to throw this error
  //     // app.renderErrorMessages('alert_error', { message: 'Your maintence schedule has one or more problems:', httpErrors: paveLifeObj.httpErrors }, 'alt_' + alternative.index);
  //   } else {
  //     //TODO: Handle alert cleanup
  //     // $(".alert").remove();
  //   }
  // }

  const drawAnalysisPeriod = function (analysisPeriod: number) {
    // Add a line for the analysis period
     d3.select(altBaseId + '_analysisEnd')
      .selectAll('*')
      .remove();

    const analysisPeriodGroup = d3.select(altBaseId + '_analysisEnd')
      .attr("transform", function (d: number, i: number) {
        return 'translate(' + (x(analysisPeriod) - 2) + ', 0)';
      });

    const analysisPeriodRect = analysisPeriodGroup.insert("rect",":first-child")
      .attr("height", height)
      .attr("width", 4)
      .attr("fill", "black")
      .attr("fill-opacity", 0.5)
      .attr("cursor", "ew-resize");

    const analysisPeriodText = analysisPeriodGroup.insert("text",':first-child')
      .text(function () {
        return 'Analysis Length: ' + analysisPeriod + ' years';
      })
      .attr("transform", 'translate(10, 5)');

    analysisPeriodRect.call(
      d3.drag()
        .container(g.node())
        .on("drag", analysisPeriodDrag)
        .on("end", analysisPeriodEndDrag)
    );


    function analysisPeriodDrag() {
      //@ts-ignore
      d3.select(this.parentElement).attr("transform", function () {
        return 'translate(' + (x(d3.event.x >= 0) ? d3.event.x : 0) + ', 0)';
      });
      //@ts-ignore
      d3.select(this.parentElement).select("text").text('End of Analysis: Year ' + dragToYears(0));
      analysisPeriod = dragToYears(0);

      //TODO: Handle this guy here too
      // displayAlternativeErrors({}, '', false);
    }

    function analysisPeriodEndDrag() {
      //TODO: handle end drag
      console.log("Finish Drag")
      // app.router.renderAlternativesTab();
    }
  };

  drawAnalysisPeriod(analysisPeriod);

  //Draw activities
  const drawActs = function () {
    //make bars for activities

    alternative.activities.forEach((a: ILccaActivityImperial, i: number) => {

      // console.log("Bar for", a.name, alternative.index, a.index, i);

      const actBaseId = '#alt_' + alternative.index + '_act_' + a.index;
      const containerId = actBaseId + '_container';
      const deleteId = actBaseId + '_delete';
      const nameId = actBaseId + '_name';
      const editId = actBaseId + '_edit';
      const copyId = actBaseId + '_copy';



      // Remove old bars
     d3.select(actBaseId + '_bar')
        .selectAll('g rect')
        .remove();

     console.log("i", i);

     // Select bar container and prep for adding/re-adding new elements
      const bar = d3.select(actBaseId + '_bar')
        .attr("class", "bar alt" + alternative.index + " act" + i)
        .attr("transform", "translate(" + x(a.startYear) + ", " + (y(i)) + ")");


      // Structural life bar
      // console.log("dims", barHeight, x(a.structuralLife_yrs), colors[i]);
      const structuralLife = bar.insert("rect", ':first-child')
        .attr("class", "structuralLife_yrs alt" + a.alternative + " act" + a.index)
        .attr("height", barHeight)
        .attr("width", x(a.structuralLife_yrs))
        .attr("fill", colors[i])
        .attr("fill-opacity", 0.2);


      // Service life bar
      const serviceLife = bar.insert("rect", ":first-child")
        .attr("class", "serviceLife_yrs alt" + a.alternative + " act" + a.index)
        .attr("height", barHeight)
        .attr("width", x(a.serviceLife_yrs))
        .attr("fill", colors[i])
        .attr("fill-opacity", 0.2);

      // Handle at end of structural life
      const structuralLifeEnd = bar.append("rect")
        .attr("class", "structuralLifeEnd alt" + a.alternative + " act" + a.index)
        .attr("height", barHeight)
        .attr("width", 4)
        .attr("transform", "translate(" + (x(a.structuralLife_yrs) - 4) + ")")
        .attr("fill", colors[i])
        .attr("fill-opacity", 0.2)
        .attr("cursor", "ew-resize")
        .on("mouseover", addDraggingAlert(a, 'structuralLife_yrs', 'Structural Life'))
        .on("mouseout", function (activity: ILccaActivityImperial) {
          mouseOut();
        });

      // handle at end of service life
      const serviceLifeEnd = bar.append("rect")
        .attr("class", "serviceLifeEnd alt" + a.alternative + " act" + a.index)
        .attr("height", barHeight)
        .attr("width", 4)
        .attr("transform","translate(" + (x(a.serviceLife_yrs) - 4) + ")")
        .attr("fill", colors[i])
        .attr("fill-opacity", 0.2)
        .attr("cursor", "ew-resize")
        .on("mouseover", addDraggingAlert(a, 'serviceLife_yrs', 'Service Life'))
        .on("mouseout", mouseOut());


    // Start year
      const startYear = bar.append("rect")
        .attr("class", "startYear")
        .attr("height", barHeight)
        .attr("width", 10)
        .attr("fill", colors[i])
        .attr("cursor", "ew-resize")
        .on("mouseover", addDraggingAlertStartYear(a))
        .on("mouseout", mouseOut());


      // Activity name
      d3.select(nameId)
        .text(a.name)
        .attr("text-anchor", "end")
        .attr("alignment-baseline", "middle")
        .attr("y", barHeight / 2)
        .attr("x", -10);


    // // pencil icon
      d3.select(editId)
        .attr('font-family', 'FontAwesome')
        .text('\uf044')
        .attr("fill", "#337ab7")
        .attr("y", 25)
        .attr("x", 25);

    // // duplicate icon
      d3.select(copyId)
      .attr('font-family', 'FontAwesome')
      .text('\uf0c5')
      .attr("font-size", 18)
      .attr("fill", "#337ab7")
      .attr("y", 25)
      .attr("x", 55);


      // Close button
      d3.select(deleteId)
        .attr('font-family', 'FontAwesome')
        .text('\uf057')
        .attr("text-anchor", "end")
        .attr("fill", "#d9534f")
        .attr("x", () => {
          return a.structuralLife_yrs > a.serviceLife_yrs ? x(a.structuralLife_yrs) - 12 : x(a.serviceLife_yrs) - 12
        })
        .attr("y", 25);

      // Drag start year
      startYear.call(
        d3.drag()
          .container(g.node())
          .on("start", addDraggingAlertStartYear(a))
          // .on("drag", startYearDrag(a))
          // .on("end", endStartYearDrag(a))
      );

      //Drag serviceLife_yrs
      serviceLifeEnd.call(
        d3.drag()
          .container(g.node())
          // .on("drag", lifeDrag(a, 'serviceLife_yrs', 'Service Life'))
          // .on("end", function (activity: ILccaActivityImperial) {
          //   endDrag(activity, 'serviceLife_yrs', 'Service Life');
          // })
      );

      // Drag structural life
      structuralLifeEnd.call(
        d3.drag()
          .container(g.node())
          // .on("drag", lifeDrag(a, 'structuralLife_yrs', 'Structural Life'))
          // .on("end", endDrag(a, 'structuralLife_yrs', 'Structural Life'))
      );

    });

    //Start Drag
    function addDraggingAlert(activity: ILccaActivityImperial, key: string, string: string) {
      // displayAlternativeErrors(activity, key, false);
      draggingText.text(function () {
        //@ts-ignore
        return 'Activity "' + activity.name + '" contribution to ' + string + ' : ' + activity[key] + ' years';
      });
    }

    //end drag
    function endDrag(activity: ILccaActivityImperial, key: string, text: string) {
      const year = indexedLifeYears(activity);
      //@ts-ignore
      activity[key] = year; // Save via activity being a pointer, and save locally.
      // alternatives[activity.altIndex].activities[activity.index] = activity; // (almost) redundant line to save directly into app.data, because on rare occasion, the pointer gets broken.
      draggingText.text("");
      // bar.remove();
      drawActs();
    }

    function mouseOut() {
      draggingText.text("");
    }

    function lifeDrag(activity: ILccaActivityImperial, key: string, string: string) {
      //check that value is positive for moving elements
      if (d3.event.x - x(activity.startYear) === findXCoordLife(activity)) {
        // Change width
        d3.select('.' + key + '.alt' + activity.alternative + '.act' + activity.index).attr("width", function () {
          return findXCoordLife(activity);
        });

        // Move handle
        d3.select('.' + key + 'End.alt' + activity.alternative + '.act' + activity.index).attr("transform", function () {
          const location = findXCoordLife(activity) - 4;
          return "translate(" + location + ")";
        });

        // Move X
        if (key === 'structuralLife_yrs' && (activity.serviceLife_yrs < xInverse(d3.event.x) - activity.startYear)) { // If structural life is longer
          // select and move x
          d3.select(".removeAct.alt" + activity.alternative + ".act" + activity.index).attr("x", (d3.event.x >= x(activity.startYear)) ? findXCoordLife(activity) - 12 : (x(activity.startYear) - 12));
        } else if (key === 'serviceLife_yrs' && (activity.structuralLife_yrs < xInverse(d3.event.x) - activity.startYear)) { // If service life is longer
          // select and move x
          d3.select(".removeAct.alt" + activity.alternative + ".act" + activity.index).attr("x", (d3.event.x >= x(activity.startYear)) ? findXCoordLife(activity) - 12 : (x(activity.startYear) - 12));
        }
      }
      // displayAlternativeErrors(activity, key, true);

      draggingText.text(function () {
        return 'Activity "' + activity.name + '" contribution to ' + string + ' : ' + indexedLifeYears(activity) + ' years';
      });
    }
    //
    // structuralLifeEnd.call(
    //   d3.drag()
    //     .container(g.node())
    //     .on("drag", function (activity: ILccaActivityImperial) {
    //       lifeDrag(activity, 'structuralLife_yrs', 'Structural Life');
    //     })
    //     .on("end", function (activity: ILccaActivityImperial) {
    //       endDrag(activity, 'structuralLife_yrs', 'Structural Life', allAlternatives);
    //     })
    // );
    //
    // //Drag serviceLife_yrs
    // serviceLifeEnd.call(
    //   d3.drag()
    //     .container(g.node())
    //     .on("drag", function (activity: ILccaActivityImperial) {
    //       lifeDrag(activity, 'serviceLife_yrs', 'Service Life');
    //     })
    //     .on("end", function (activity: ILccaActivityImperial) {
    //       endDrag(activity, 'serviceLife_yrs', 'Service Life', allAlternatives);
    //     })
    // );
    //
    // //Drag start year
    // startYear.call(
    //   d3.drag()
    //     .container(g.node())
    //     // .on("start", function (activity) {
    //     //     addDraggingAlertStartYear(activity);
    //     //   })
    //     .on("drag", startYearDrag)
    //     .on("end", endStartYearDrag)
    // );

    function addDraggingAlertStartYear(activity: ILccaActivityImperial) {
      // displayAlternativeErrors(activity, 'startYear', false);
      draggingText.text(function () {
        return activity.name + ' starts in year: ' + activity.startYear;
      });
    }

    function rightLimStartYear(activity: ILccaActivityImperial) {
      return (activity.structuralLife_yrs >= activity.serviceLife_yrs) ? x(analysisPeriod * 1.5 - activity.structuralLife_yrs) : x(analysisPeriod * 1.5 - activity.serviceLife_yrs);
    }

    //returns the position of the start year relative to the axes, in pixels
    function findXCoordStartYear(activity: ILccaActivityImperial) {
      const xPosition = d3.mouse(g.node())[0];
      if (xPosition >= 0) { //check left lim
        if (xPosition <= rightLimStartYear(activity)) { // check right lim
          return xPosition;
        } else { // return right lim
          return rightLimStartYear(activity);
        }
      } else { // return left lim
        return 0;
      }
    }

    // Returns the width in pixesls
    function findXCoordLife(activity: ILccaActivityImperial) {
      const xPosition = d3.mouse(g.node())[0];
      if (xPosition >= x(activity.startYear)) { //check left lim
        if (xPosition <= x(analysisPeriod * 1.5)) { // check right lim
          return xPosition - x(activity.startYear);
        } else { // return right lim
          return x(analysisPeriod * 1.5 - activity.startYear);
        }
      } else { // return left lim
        return 0;
      }
    }

    function indexedStartYears(activity: ILccaActivityImperial) {
      return Math.round(xInverse(findXCoordStartYear(activity)));
    }

    function indexedLifeYears(activity: ILccaActivityImperial) {
      return Math.round(xInverse(findXCoordLife(activity)));
    }

    function startYearDrag(activity: ILccaActivityImperial) {
      // Drag function
      //@ts-ignore
      d3.select(this.parentElement).attr("transform", function () {
      // element.attr("transform", function () {
        const string = "translate(";
        const xCoord = findXCoordStartYear(activity);
        const yCoord = (y(activity.index) - Math.round(barHeight / 2));
        return "translate(" + xCoord + ", " + yCoord + ")";
      });

      // displayAlternativeErrors(activity, 'startYear', true);
      draggingText.text(function () {
        return activity.name + ' starts in year ' + indexedStartYears(activity);
      });
    }

    function endStartYearDrag(activity: ILccaActivityImperial) {
      const startYearAct = indexedStartYears(activity);
      activity.startYear = startYearAct;
      // allAlternatives[activity.altIndex].activities[activity.index] = activity;
      draggingText.text("");
      // bar.remove();
      drawActs();
    }
  };

  drawActs();

  // g.append("g")
  d3.select(altBaseId + '_xAxis')
    .attr("class", "axis axis--x")
    .attr("transform", "translate(0," + height + ")")
    .call(d3.axisBottom(x));

  // g.append("text")
  //   .text("Years since " + beginningAnalysisPeriod + " (Beginning Of Analysis Period)")
  //   .attr("text-anchor", "middle")
  //   .attr("style", "font-style:oblique; font-size:14px")
  //   .attr("y", height + 40)
  //   .attr("x", function () {
  //     return width / 2;
  //   });
};

interface IPaveLife {
  errors: string[],
  structuralLifespan: number,
  serviceLifespan: number,
}

const checkAltPaveLife = function (alternative: ILccaAlternativeImperial, analysisPeriod: number): IPaveLife {
  const sortedByStart = alternative.activities.sort((a, b) => a.startYear > b.startYear ? 1 : 0);
  const errors = [];
  let structuralLifespan = 0;
  let serviceLifespan = 0;

  // If checking structural life, just add up contributions and make sure you have no gaps
  for (let i = 0; i < sortedByStart.length; i++) {
    if (sortedByStart[i].startYear > structuralLifespan) { // Gap in structural life;
      errors.push('Gap in maintence: Structural Life reaches zero in year ' + structuralLifespan + ' following ' + ((sortedByStart[i - 1]) ? sortedByStart[i - 1].name : "the beginning of the analysis period") + ' before ' + sortedByStart[i].name + ' in year ' + sortedByStart[i].startYear + '.');
      // return [false, structuralLifespan, sortedByStart[i].startYear, ((sortedByStart[i - 1]) ? sortedByStart[i - 1].name : "the beginning of the analysis period"), sortedByStart[i].name];
    } else if ((sortedByStart[i].startYear > serviceLifespan)) { // Gap in service life
      errors.push('Gap in maintence: Service Life reaches zero in year ' + serviceLifespan + ' following ' + ((sortedByStart[i - 1]) ? sortedByStart[i - 1].name : "the beginning of the analysis period") + ' before ' + sortedByStart[i].name + ' in year ' + sortedByStart[i].startYear);
      // return [false, serviceLifespan, sortedByStart[i].startYear, ((sortedByStart[i - 1]) ? sortedByStart[i - 1].name : "the beginning of the analysis period"), sortedByStart[i].name];
    } else { //Add to pavement health.
      if (sortedByStart[i].structuralLife_yrs > 0 && serviceLifespan > sortedByStart[i].startYear) {
        errors.push('Structural life improved by ' + sortedByStart[i].name + ', with ' + (serviceLifespan - (sortedByStart[i].startYear)) + ' years of service life remaining from ' + sortedByStart[i - 1].name + '. Service life reset to zero at year ' + sortedByStart[i].startYear + ' on the presumption that surface is removed for structural maintence.');
        serviceLifespan = sortedByStart[1].startYear;
      }
    }
    serviceLifespan += sortedByStart[i].serviceLife_yrs;
    structuralLifespan += sortedByStart[i].structuralLife_yrs;
  }
  if (structuralLifespan < analysisPeriod) {
    errors.push('Gap in maintence: Structural Life reaches zero at year ' + structuralLifespan + ', before the end of the analysis period in year ' + analysisPeriod + '.');
  } else if (serviceLifespan < analysisPeriod) {
    errors.push('Gap in maintence: Service Life reaches zero at year ' + serviceLifespan + ', before the end of the analysis period in year ' + analysisPeriod + '.');
  }
  return {errors: errors, structuralLifespan: structuralLifespan, serviceLifespan: serviceLifespan};
};
